// ToggleSwitch.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import StyledText from './StyledText';
import { useTheme } from '../../middleware/ThemeContext';

interface ToggleSwitchProps {
    label?: string;
    showTitle?: boolean;
    onChange?: (isChecked: boolean) => void;
    checked?: boolean;
    activeColor?: string;    // e.g., '#4CAF50'
    inactiveColor?: string;  // e.g., '#ccc'
    knobColor?: string;      // optional: slider knob color (default: white)
    disabled?: boolean;
    noPaddingNeeded?: boolean;
}

interface SliderProps {
    $isActive: boolean;
    $activeColor: string;
    $inactiveColor: string;
    $knobColor: string;
}

const ToggleContainer = styled.div<{ noPaddingNeeded?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${(props) => props.noPaddingNeeded ? '0px' : '12px 16px'};
  border-radius: 8px;
  font-family: Arial, sans-serif;
`;

const ToggleLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const Slider = styled.span<SliderProps>`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) => (props.$isActive ? props.$activeColor : props.$inactiveColor)};
  transition: 0.4s;
  border-radius: 34px;

  &::before {
    position: absolute;
    content: "";
    height: 14px;
    width: 14px;
    left: 2px;
    bottom: 2px;
    background-color: ${(props) => props.$knobColor};
    transition: 0.4s;
    border-radius: 50%;
    transform: ${(props) => (props.$isActive ? 'translateX(14px)' : 'translateX(0)')};
  }
`;

const Text = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
    label = "Do you want to activate this widget?",
    showTitle = true,
    onChange,
    checked: controlledChecked,
    activeColor = "#4CAF50",
    inactiveColor = "#ccc",
    knobColor = "#fff",
    disabled,
    noPaddingNeeded
}) => {
    const { theme } = useTheme();
    const [isChecked, setIsChecked] = useState<boolean>(!!controlledChecked);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.checked;
        if (controlledChecked === undefined) {
            setIsChecked(newValue);
        }
        onChange?.(newValue);
    };

    const isToggled = controlledChecked !== undefined ? controlledChecked : isChecked;

    return (
        <ToggleContainer noPaddingNeeded={noPaddingNeeded}>
            <ToggleLabel>
                <input
                    type="checkbox"
                    checked={isToggled}
                    onChange={handleChange}
                    disabled={disabled}
                />
                <Slider
                    $isActive={isToggled}
                    $activeColor={activeColor}
                    $inactiveColor={inactiveColor}
                    $knobColor={knobColor}
                />
            </ToggleLabel>
            {showTitle &&
                <StyledText fontSize="12px" fontWeight={500} textColor={theme.CorporateTheme.colors.headerColor}>{label}</StyledText>
            }
        </ToggleContainer>
    );
};

export default ToggleSwitch;